import React, { useState } from 'react';
import { Table, Form, Button, InputGroup, Modal, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { useRowLock } from '../context/RowLockContext';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';

interface Client {
  id: number;
  auditClientCode: string;
  companySecretarialCode: string;
  companyName: string;
  partner: string;
  group: string;
  brNumber: string;
  stockTake: string;
  latestAnnualAuditYearEndDate: string;
  taxReturnsOutstanding: string;
  taxReturnsOutstandingYear: string;
  annualReturnFiled: string;
  agent: string;
  registeredOffice: string;
  clientContact: string;
  active: boolean;
  services: string[];
}

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const { clients, addClient, updateClient } = useAppContext();
  const { lockRow, unlockRow, isRowLocked, getLockOwner } = useRowLock();
  const { currentUser } = useAuth();

  // State for search inputs
  const [searchAuditCode, setSearchAuditCode] = useState('');
  const [searchCompanySecretarialCode, setSearchCompanySecretarialCode] = useState('');
  const [searchCompanyName, setSearchCompanyName] = useState('');
  const [searchBrNumber, setSearchBrNumber] = useState('');

  // State for editing
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  
  // State for modal dialog
  const [showClientModal, setShowClientModal] = useState(false);
  const [modalClient, setModalClient] = useState<Client | null>(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 10 items per page

  // State for new client modal
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [companyNameError, setCompanyNameError] = useState('');

  // State for sorting
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Handle sorting
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to clients
  const sortedFilteredClients = () => {
    const filtered = clients.filter(client => {
      return (
        client.auditClientCode.toLowerCase().includes(searchAuditCode.toLowerCase()) &&
        client.companySecretarialCode.toLowerCase().includes(searchCompanySecretarialCode.toLowerCase()) &&
        client.companyName.toLowerCase().includes(searchCompanyName.toLowerCase()) &&
        client.brNumber.toLowerCase().includes(searchBrNumber.toLowerCase())
      );
    });

    if (!sortConfig) return filtered;

    return [...filtered].sort((a, b) => {
      let aValue = a[sortConfig.key as keyof Client];
      let bValue = b[sortConfig.key as keyof Client];

      // Handle boolean values
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        if (aValue === bValue) return 0;
        return sortConfig.direction === 'asc' ? (aValue ? -1 : 1) : (aValue ? 1 : -1);
      }

      // Handle string values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      const aStr = String(aValue);
      const bStr = String(bValue);

      // Check if both values are numeric
      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum < bNum) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aNum > bNum) return sortConfig.direction === 'asc' ? 1 : -1;
      } else {
        // String comparison
        const comparison = aStr.localeCompare(bStr);
        if (comparison !== 0) {
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });
  };

  // Pagination logic
  const sortedClients = sortedFilteredClients();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClients = sortedClients.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage);

  // Handle Excel export
  const handleExportExcel = () => {
    const allClients = sortedFilteredClients(); // Export all filtered and sorted data
    const ws = XLSX.utils.json_to_sheet(allClients);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, 'clients.xlsx');
  };

  // Handle edit field
  const handleEditField = (clientId: number, field: keyof Client, value: any) => {
    updateClient(clientId, field, value);
  };

  // Start editing a field
  const startEditing = (client: Client, field: string) => {
    // Try to lock the row for editing
    if (lockRow('clientList', client.id)) {
      setEditingClient(client);
      setEditingField(field);
    } else {
      // Row is locked by another user, show a message
      const lockOwner = getLockOwner('clientList', client.id);
      alert(`${t('rowLockedBy')} ${lockOwner}`);
    }
  };

  // Stop editing
  const stopEditing = () => {
    if (editingClient) {
      unlockRow('clientList', editingClient.id);
    }
    setEditingClient(null);
    setEditingField(null);
  };
  
  // Open client modal
  const openClientModal = (client: Client) => {
    setModalClient(client);
    setShowClientModal(true);
  };
  
  // Close client modal
  const closeClientModal = () => {
    setShowClientModal(false);
    setModalClient(null);
  };
  
  // Save client changes
  const saveClientChanges = () => {
    if (modalClient) {
      // Update each field of the client
      Object.keys(modalClient).forEach(key => {
        if (key !== 'id') {  // Skip id as it shouldn't be updated
          updateClient(modalClient.id, key as keyof Client, modalClient[key as keyof Client]);
        }
      });
      closeClientModal();
    }
  };
  
  // Handle modal client field change
  const handleModalClientChange = (field: keyof Client, value: any) => {
    if (modalClient) {
      setModalClient({
        ...modalClient,
        [field]: value
      });
    }
  };

  // Validate company name (must start with English letter)
  const validateCompanyName = (name: string): boolean => {
    const regex = /^[A-Za-z]/;
    return regex.test(name);
  };

  // Handle service selection
  const handleServiceChange = (service: string) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter(s => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  // Validate required services
  const hasRequiredService = (): boolean => {
    return ['audit', 'account only', 'account + audit', 'company secretary'].some(service => 
      selectedServices.includes(service)
    );
  };

  // Generate audit client code based on first character of company name
  const generateAuditClientCode = (companyName: string, existingClients: Client[]): string => {
    const firstChar = companyName.charAt(0).toUpperCase();
    
    // Count how many existing clients start with the same character
    const sameCharClients = existingClients.filter(client => 
      client.companyName.charAt(0).toUpperCase() === firstChar
    );
    
    // The new client will be the next in sequence
    const sequenceNumber = sameCharClients.length + 1;
    
    return `${firstChar}${sequenceNumber.toString().padStart(3, '0')}`;
  };

  // Generate company secretarial code based on audit client code
  const generateCompanySecretarialCode = (auditCode: string): string => {
    return `C${auditCode}`;
  };

  // Add new client
  const addNewClient = () => {
    if (!validateCompanyName(newCompanyName)) {
      setCompanyNameError('Company name must start with an English letter');
      return;
    }
    
    if (!hasRequiredService()) {
      alert('Please select at least one of: audit, account only, account + audit, or company secretary');
      return;
    }

    // Generate new codes
    const newAuditCode = generateAuditClientCode(newCompanyName, clients);
    const newCompanySecretarialCode = generateCompanySecretarialCode(newAuditCode);

    const newClient: Client = {
      id: clients.length > 0 ? Math.max(...clients.map(c => c.id)) + 1 : 1,
      auditClientCode: newAuditCode,
      companySecretarialCode: newCompanySecretarialCode,
      companyName: newCompanyName,
      partner: 'TBD',
      group: 'Group ' + String.fromCharCode(64 + (clients.length + 1)),
      brNumber: 'TBD',
      stockTake: 'TBD',
      latestAnnualAuditYearEndDate: 'TBD',
      taxReturnsOutstanding: 'TBD',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'TBD',
      agent: 'TBD',
      registeredOffice: 'TBD',
      clientContact: 'TBD',
      active: true,
      services: selectedServices
    };

    addClient(newClient);
    setShowNewClientModal(false);
    setNewCompanyName('');
    setSelectedServices([]);
    setCompanyNameError('');

    // Determine which tab to navigate to based on selected services
    let tab = 'audit'; // Default to audit
    if (selectedServices.includes('company secretary') && 
        !selectedServices.includes('audit') && 
        !selectedServices.includes('account') && 
        !selectedServices.includes('account only') && 
        !selectedServices.includes('account + audit')) {
      tab = 'secretarial';
    } else if ((selectedServices.includes('account') || 
                selectedServices.includes('account only') || 
                selectedServices.includes('account + audit')) && 
               !selectedServices.includes('audit') && 
               !selectedServices.includes('company secretary')) {
      tab = 'accounting';
    }
    // If multiple service types are selected, default to audit

    // Navigate to Engagement page with the appropriate tab
    navigate(`/engagement?tab=${tab}`);
  };

  const { t } = useLanguage();

  return (
    <div className="client-list">
      <Container>
        <h2>{t('clientList')}</h2>

        <div className="d-flex justify-content-between mb-3">
          <Button
            variant="primary"
            onClick={() => setShowNewClientModal(true)}
          >
            {t('addNewClient')}
          </Button>
          {/* <Button
            variant="success"
            onClick={handleExportExcel}
          >
            Export to Excel
          </Button> */}
        </div>

        {/* Client Table */}
        <Table striped hover responsive className="no-left-right-borders">
          <thead>
            <tr>
              <th
                onClick={() => handleSort('id')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  #
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'id' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(編號)</div>
                {/* Search input for ID column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for ID column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('auditClientCode')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('auditClientCode')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'auditClientCode' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(審計客戶代碼)</div>
                {/* Search input for Audit Client Code column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value={searchAuditCode}
                    onChange={(e) => {
                      setSearchAuditCode(e.target.value);
                      setCurrentPage(1); // Reset to first page when search changes
                    }}
                    style={{ paddingRight: '30px' }}
                  />
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                </div>
              </th>
              <th
                onClick={() => handleSort('companySecretarialCode')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('companySecretarialClientCode')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'companySecretarialCode' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(公司秘書客戶代碼)</div>
                {/* Search input for Company Secretarial Code column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value={searchCompanySecretarialCode}
                    onChange={(e) => {
                      setSearchCompanySecretarialCode(e.target.value);
                      setCurrentPage(1); // Reset to first page when search changes
                    }}
                    style={{ paddingRight: '30px' }}
                  />
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                </div>
              </th>
              <th
                onClick={() => handleSort('companyName')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('companyName')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'companyName' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(公司名稱)</div>
                {/* Search input for Company Name column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value={searchCompanyName}
                    onChange={(e) => {
                      setSearchCompanyName(e.target.value);
                      setCurrentPage(1); // Reset to first page when search changes
                    }}
                    style={{ paddingRight: '30px' }}
                  />
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                </div>
              </th>
              <th
                onClick={() => handleSort('partner')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('partner')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'partner' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(合夥人)</div>
                {/* Search input for Partner column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for Partner column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('group')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('group')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'group' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(組別)</div>
                {/* Search input for Group column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for Group column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('brNumber')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('brNumber')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'brNumber' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(商業登記號碼)</div>
                {/* Search input for BR Number column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value={searchBrNumber}
                    onChange={(e) => {
                      setSearchBrNumber(e.target.value);
                      setCurrentPage(1); // Reset to first page when search changes
                    }}
                    style={{ paddingRight: '30px' }}
                  />
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                </div>
              </th>
              <th
                onClick={() => handleSort('stockTake')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('stockTake')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'stockTake' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(盤點)</div>
                {/* Search input for Stock Take column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for Stock Take column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('latestAnnualAuditYearEndDate')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('latestAnnualAuditYearEndDate')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'latestAnnualAuditYearEndDate' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(最新年度審計年結日期)</div>
                {/* Search input for Latest Annual Audit Year End Date column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('taxReturnsOutstanding')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('taxReturnsOutstanding')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'taxReturnsOutstanding' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(未完成的稅務申報)</div>
                {/* Search input for Tax Returns Outstanding column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('annualReturnFiled')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('annualReturnAlreadyFiled')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'annualReturnFiled' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(已在公司註冊處提交年度申報表)</div>
                {/* Search input for Annual Return Filed column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('agent')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('agent')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'agent' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(代理人)</div>
                {/* Search input for Agent column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('registeredOffice')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('registeredOffice')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'registeredOffice' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(註冊辦事處)</div>
                {/* Search input for Registered Office column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('clientContact')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('clientContact')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'clientContact' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(客戶聯絡人)</div>
                {/* Search input for Client Contact column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('active')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('active')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'active' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(活躍)</div>
                {/* Search input for Active column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
              <th
                onClick={() => handleSort('services')}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  {t('services')}
                  <span style={{ marginLeft: '5px' }}>
                    {sortConfig?.key === 'services' ? (
                      sortConfig.direction === 'asc' ? <FontAwesomeIcon icon={faSortUp} /> : <FontAwesomeIcon icon={faSortDown} />
                    ) : (
                      <FontAwesomeIcon icon={faSort} />
                    )}
                  </span>
                </div>
                <div className="text-muted small">(服務)</div>
                {/* Search input for Services column */}
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Form.Control
                    type="text"
                    placeholder=""
                    value="" // No search for this column
                    readOnly
                    style={{ paddingRight: '30px' }}
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentClients.map((client) => {
              const isLocked = isRowLocked('clientList', client.id);
              const lockOwner = isLocked ? getLockOwner('clientList', client.id) : null;
              const isCurrentUserEditing = editingClient?.id === client.id;

              return (
                <tr
                  key={client.id}
                  onClick={() => openClientModal(client)}
                  style={{
                    border: isLocked
                      ? (isCurrentUserEditing ? '2px solid #008000' : '2px solid #ff0000')
                      : 'none',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  {isLocked && !isCurrentUserEditing && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 0, 0, 0.1)', // Red for locked by another user
                        zIndex: 1,
                        pointerEvents: 'none',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#ff0000',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }}
                    >
                      {`${t('lockedBy')} ${lockOwner}`}
                    </div>
                  )}
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {client.id}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'auditClientCode' ? (
                      <Form.Control
                        type="text"
                        value={client.auditClientCode}
                        onChange={(e) => handleEditField(client.id, 'auditClientCode', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.auditClientCode
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'companySecretarialCode' ? (
                      <Form.Control
                        type="text"
                        value={client.companySecretarialCode}
                        onChange={(e) => handleEditField(client.id, 'companySecretarialCode', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.companySecretarialCode
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'companyName' ? (
                      <Form.Control
                        type="text"
                        value={client.companyName}
                        onChange={(e) => handleEditField(client.id, 'companyName', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.companyName
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'partner' ? (
                      <Form.Select
                        value={client.partner}
                        onChange={(e) => handleEditField(client.id, 'partner', e.target.value)}
                        onBlur={stopEditing}
                        autoFocus
                      >
                        <option value="">Select Partner</option>
                        <option value="Jacky">Jacky</option>
                        <option value="Henry">Henry</option>
                        <option value="Kenneth">Kenneth</option>
                      </Form.Select>
                    ) : (
                      client.partner
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'group' ? (
                      <Form.Control
                        type="text"
                        value={client.group}
                        onChange={(e) => handleEditField(client.id, 'group', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.group
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'brNumber' ? (
                      <Form.Control
                        type="text"
                        value={client.brNumber}
                        onChange={(e) => handleEditField(client.id, 'brNumber', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.brNumber
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'stockTake' ? (
                      <Form.Control
                        type="text"
                        value={client.stockTake}
                        onChange={(e) => handleEditField(client.id, 'stockTake', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.stockTake
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'latestAnnualAuditYearEndDate' ? (
                      <Form.Control
                        type="text"
                        value={client.latestAnnualAuditYearEndDate}
                        onChange={(e) => handleEditField(client.id, 'latestAnnualAuditYearEndDate', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.latestAnnualAuditYearEndDate
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'taxReturnsOutstanding' ? (
                      <div>
                        <Form.Control
                          type="text"
                          value={client.taxReturnsOutstanding}
                          onChange={(e) => handleEditField(client.id, 'taxReturnsOutstanding', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                          style={{ marginBottom: '5px' }}
                        />
                        {client.taxReturnsOutstanding.toLowerCase() === 'yes' && (
                          <Form.Control
                            type="text"
                            placeholder="Year"
                            value={client.taxReturnsOutstandingYear}
                            onChange={(e) => handleEditField(client.id, 'taxReturnsOutstandingYear', e.target.value)}
                          />
                        )}
                      </div>
                    ) : (
                      client.taxReturnsOutstanding + (client.taxReturnsOutstanding.toLowerCase() === 'yes' && client.taxReturnsOutstandingYear ? ` (${client.taxReturnsOutstandingYear})` : '')
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'annualReturnFiled' ? (
                      <Form.Control
                        type="text"
                        value={client.annualReturnFiled}
                        onChange={(e) => handleEditField(client.id, 'annualReturnFiled', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.annualReturnFiled
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'agent' ? (
                      <Form.Control
                        type="text"
                        value={client.agent}
                        onChange={(e) => handleEditField(client.id, 'agent', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.agent
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'registeredOffice' ? (
                      <Form.Control
                        type="text"
                        value={client.registeredOffice}
                        onChange={(e) => handleEditField(client.id, 'registeredOffice', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.registeredOffice
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'clientContact' ? (
                      <Form.Control
                        type="text"
                        value={client.clientContact}
                        onChange={(e) => handleEditField(client.id, 'clientContact', e.target.value)}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      />
                    ) : (
                      client.clientContact
                    )}
                  </td>
                  <td
                    style={{
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    {editingClient?.id === client.id && editingField === 'active' ? (
                      <Form.Select
                        value={client.active.toString()}
                        onChange={(e) => handleEditField(client.id, 'active', e.target.value === 'true')}
                        onBlur={stopEditing}
                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                        autoFocus
                      >
                        <option value="true">{t('yes')}</option>
                        <option value="false">{t('no')}</option>
                      </Form.Select>
                    ) : (
                      client.active ? t('yes') : t('no')
                    )}
                  </td>
                  <td style={{ position: 'relative', zIndex: 2 }}>
                    {client.services.join(', ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {/* Pagination Controls */}
        <div className="d-flex justify-content-center mt-3">
          <nav>
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  {t('previous')}
                </button>
              </li>

              {/* Show page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  {t('next')}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </Container>

      {/* Modal for client details */}
      <Modal show={showClientModal} onHide={closeClientModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('clientDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalClient && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('auditClientCode')}<br />
                  <small className="text-muted">(審計客戶代碼)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.auditClientCode}
                  onChange={(e) => handleModalClientChange('auditClientCode', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('companySecretarialClientCode')}<br />
                  <small className="text-muted">(公司秘書客戶代碼)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.companySecretarialCode}
                  onChange={(e) => handleModalClientChange('companySecretarialCode', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('companyName')}<br />
                  <small className="text-muted">(公司名稱)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.companyName}
                  onChange={(e) => handleModalClientChange('companyName', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('partner')}<br />
                  <small className="text-muted">(合夥人)</small>
                </Form.Label>
                <Form.Select
                  value={modalClient.partner}
                  onChange={(e) => handleModalClientChange('partner', e.target.value)}
                >
                  <option value="">Select Partner</option>
                  <option value="Jacky">Jacky</option>
                  <option value="Henry">Henry</option>
                  <option value="Kenneth">Kenneth</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('group')}<br />
                  <small className="text-muted">(組別)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.group}
                  onChange={(e) => handleModalClientChange('group', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('brNumber')}<br />
                  <small className="text-muted">(商業登記號碼)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.brNumber}
                  onChange={(e) => handleModalClientChange('brNumber', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('stockTake')}<br />
                  <small className="text-muted">(盤點)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.stockTake}
                  onChange={(e) => handleModalClientChange('stockTake', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('latestAnnualAuditYearEndDate')}<br />
                  <small className="text-muted">(最新年度審計年結日期)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.latestAnnualAuditYearEndDate}
                  onChange={(e) => handleModalClientChange('latestAnnualAuditYearEndDate', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('taxReturnsOutstanding')}<br />
                  <small className="text-muted">(未完成的稅務申報)</small>
                </Form.Label>
                <Form.Select
                  value={modalClient.taxReturnsOutstanding}
                  onChange={(e) => {
                    handleModalClientChange('taxReturnsOutstanding', e.target.value);
                    // Reset year when changing to 'No'
                    if (e.target.value.toLowerCase() !== 'yes') {
                      handleModalClientChange('taxReturnsOutstandingYear', '');
                    }
                  }}
                >
                  <option value="No">{t('no')}</option>
                  <option value="Yes">{t('yes')}</option>
                </Form.Select>
                {modalClient.taxReturnsOutstanding.toLowerCase() === 'yes' && (
                  <Form.Group className="mt-2">
                    <Form.Label>Tax Returns Outstanding Year</Form.Label>
                    <Form.Control
                      type="text"
                      value={modalClient.taxReturnsOutstandingYear}
                      onChange={(e) => handleModalClientChange('taxReturnsOutstandingYear', e.target.value)}
                      placeholder="Enter year"
                    />
                  </Form.Group>
                )}
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('annualReturnAlreadyFiled')}<br />
                  <small className="text-muted">(已在公司註冊處提交年度申報表)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.annualReturnFiled}
                  onChange={(e) => handleModalClientChange('annualReturnFiled', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('agent')}<br />
                  <small className="text-muted">(代理人)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.agent}
                  onChange={(e) => handleModalClientChange('agent', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('registeredOffice')}<br />
                  <small className="text-muted">(註冊辦事處)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.registeredOffice}
                  onChange={(e) => handleModalClientChange('registeredOffice', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('clientContact')}<br />
                  <small className="text-muted">(客戶聯絡人)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={modalClient.clientContact}
                  onChange={(e) => handleModalClientChange('clientContact', e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('active')}<br />
                  <small className="text-muted">(活躍)</small>
                </Form.Label>
                <Form.Select
                  value={modalClient.active.toString()}
                  onChange={(e) => handleModalClientChange('active', e.target.value === 'true')}
                >
                  <option value="true">{t('yes')}</option>
                  <option value="false">{t('no')}</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('services')}<br />
                  <small className="text-muted">(服務)</small>
                </Form.Label>
                <div>
                  {[
                    { value: 'audit', label: 'audit (審計)' },
                    { value: 'account only', label: 'account only (僅會計)' },
                    { value: 'account + audit', label: 'account + audit (會計 + 審計)' },
                    { value: 'company secretary', label: 'company secretary (公司秘書)' },
                    { value: 'advisory', label: 'advisory (諮詢)' },
                    { value: 'taxation', label: 'taxation (稅務)' }
                  ].map(item => (
                    <Form.Check
                      key={item.value}
                      type="checkbox"
                      label={item.label}
                      checked={modalClient.services.includes(item.value)}
                      onChange={(e) => {
                        let updatedServices = [...modalClient.services];
                        if (e.target.checked) {
                          updatedServices.push(item.value);
                        } else {
                          updatedServices = updatedServices.filter(s => s !== item.value);
                        }
                        handleModalClientChange('services', updatedServices);
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeClientModal}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={saveClientChanges}>
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for adding new client */}
      <Modal show={showNewClientModal} onHide={() => setShowNewClientModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('addNewClient')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t('companyName')} *<br />
                <small className="text-muted">(公司名稱 *)</small>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t('companyMustStartWithLetter')}
                value={newCompanyName}
                onChange={(e) => {
                  setNewCompanyName(e.target.value);
                  if (validateCompanyName(e.target.value)) {
                    setCompanyNameError('');
                  }
                }}
                isInvalid={!!companyNameError}
              />
              <Form.Control.Feedback type="invalid">
                {companyNameError}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t('services')} *<br />
                <small className="text-muted">(服務 *)</small>
              </Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  id="audit"
                  label={`${t('audit')} (審計)`}
                  checked={selectedServices.includes('audit')}
                  onChange={() => handleServiceChange('audit')}
                />
                <Form.Check
                  type="checkbox"
                  id="account-only"
                  label={`${t('accountOnly')} (僅會計)`}
                  checked={selectedServices.includes('account only')}
                  onChange={() => handleServiceChange('account only')}
                />
                <Form.Check
                  type="checkbox"
                  id="account-audit"
                  label={`${t('accountPlusAudit')} (會計 + 審計)`}
                  checked={selectedServices.includes('account + audit')}
                  onChange={() => handleServiceChange('account + audit')}
                />
                <Form.Check
                  type="checkbox"
                  id="company-secretary"
                  label={`${t('companySecretary')} (公司秘書)`}
                  checked={selectedServices.includes('company secretary')}
                  onChange={() => handleServiceChange('company secretary')}
                />
                <Form.Check
                  type="checkbox"
                  id="advisory"
                  label={`${t('advisory')} (諮詢)`}
                  checked={selectedServices.includes('advisory')}
                  onChange={() => handleServiceChange('advisory')}
                />
                <Form.Check
                  type="checkbox"
                  id="taxation"
                  label={`${t('taxation')} (稅務)`}
                  checked={selectedServices.includes('taxation')}
                  onChange={() => handleServiceChange('taxation')}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewClientModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={addNewClient}>
            {t('add')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClientList;